import React, { useEffect, useState } from 'react';
import { BadgeInfo, Copy, Download, Loader } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../reducers';
import { USER_PACKAGE } from '../../plan/userPackage';
import { config } from '../../../../config';
import { getPaymentHistory } from '../../../services/subscriptionServices';
import {
  CancellationConfirmModal,
  StripeSurveyModal,
  RetentionOfferModal,
  CancellationSuccessModal,
  useCancellationFlow,
} from './cancellation';
import logger from '../../../lib/logger';
import UpsellButton from '../../dashboard/toolbar/UpsellButton';
import UpgradeButton from '../../dashboard/toolbar/UpgradeButton';
import planFeatures from '../../plan/upgrade/upgradeView/featureLimits';
import { Button } from '../../../components/ui/button';
import { Separator } from '../../../components/ui/separator';
import {
  SettingsSection,
  SettingsLayout,
} from '../../../components/ui/settings-section';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Card, CardContent } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import ContactSalesForm from '../../sales/ContactSalesForm';
import ActionDialog from '../../globalModals/ActionDialog';
import { trackDownloadInvoiceButton } from '../../plan/planSlice';
import { ContactSupportDialog } from '../../support';
import ChangePlanModal from './ChangePlanModal';
import { IPayment } from '../../../sdk/client/api/subscription/types';
import { usePlan } from '../../plan/hooks/plan';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { useUpdateQuery, useQuery } from '../../../hooks/urlHooks';
import ActivateTrialButton from './ActivateTrialButton';

interface BillingProps {
  onClose?: () => void;
}

const Billing: React.FC<BillingProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isPlan } = usePlan();
  const { plan, team, settings } = useSelector((state: RootState) => state);
  const modalStack = useSelector(
    (state: RootState) => state.globalModals.modalStack,
  );
  const { stripePaymentUpdateUrl } = config;
  const { teamId } = team.selectedTeam || {};
  const {
    package: teamPackage,
    term,
    price: currentPrice,
    provider: subscriptionProvider,
    state: subscriptionState,
  } = plan.subscription || {};
  const { providerId } = plan.subscription || {};
  const { payer } = plan;
  const currentUserId = settings.user._id;
  const isOwner = currentUserId === team.selectedTeam?.ownerId;
  const payerUserId = payer?.userId || plan.subscription?.payerUserId;
  const isPayer = payerUserId ? currentUserId === payerUserId : isOwner;
  const isFreeUser: boolean = teamPackage === USER_PACKAGE.FREE_LOGGEDIN;
  const isTrialing: boolean = subscriptionState === 'trialing';
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Cancellation flow (shared hook)
  const {
    showConfirmModal,
    showSurveyModal,
    showRetentionModal,
    showSuccessModal,
    cancellationInfo,
    cancelDate,
    successMessage,
    cancelFailed,
    isScheduledForCancellation,
    cancellationDate,
    hasActiveDiscount,
    reactivating,
    reactivateFailed,
    handleCancelPlan,
    handleConfirmProceed,
    handleSurveySubmit,
    handleAcceptRetentionOffer,
    handleDeclineRetentionOffer,
    handleReactivateSubscription,
    closeAllModals,
  } = useCancellationFlow();

  const { openUpgrade, source, returnTo } = useQuery();
  const updateQuery = useUpdateQuery();
  const isUpgradeModalOpen = modalStack.includes(
    'stripe-pricing-table-upgrade-modal',
  );

  // Check if user is subscribed through mobile payment provider
  const isMobileProvider =
    subscriptionProvider === 'apple' || subscriptionProvider === 'android';

  useEffect(() => {
    const shouldOpen =
      openUpgrade === '1' || openUpgrade?.toLowerCase() === 'true';

    if (!shouldOpen || isUpgradeModalOpen) {
      return;
    }

    if (returnTo) {
      sessionStorage.setItem('externalUpgradeReturn', returnTo);
    }

    dispatch(
      pushModal({
        type: 'stripe-pricing-table-upgrade-modal',
        source,
      }),
    );

    updateQuery({
      openUpgrade: undefined,
      returnTo: undefined,
      source: undefined,
    });
  }, [
    openUpgrade,
    returnTo,
    source,
    dispatch,
    updateQuery,
    isUpgradeModalOpen,
  ]);

  const fetchPaymentHistory = async () => {
    if (!teamId || isFreeUser) {
      return;
    }
    try {
      setLoadingPayments(true);
      setPaymentError(null);
      const paymentHistory = await getPaymentHistory({ teamId });
      setPayments(paymentHistory);
    } catch (error) {
      logger.error('Failed to fetch payment history', error);
      setPaymentError('Failed to load payment history.');
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleOpenPaymentHistory = () => {
    setPaymentHistoryOpen(true);
    if (payments.length === 0 && !loadingPayments && !paymentError) {
      fetchPaymentHistory();
    }
  };

  const currentPricePerMonth =
    term === 'monthly' ? currentPrice : currentPrice / 12;

  // Helper function to check if subscription is managed through Apple
  const isAppleSubscription = (subscriptionProviderId: string) => {
    // Apple subscription IDs are typically 16-digit numbers starting with "2"
    // Example: 2000000886091627
    return (
      subscriptionProviderId && /^2[0-9]{15}$/.test(subscriptionProviderId)
    );
  };

  const formatPackageName = (packageName: string) => {
    if (!packageName) return '';

    if (
      packageName === USER_PACKAGE.FREE_LOGGEDIN ||
      packageName === USER_PACKAGE.FREE
    ) {
      return 'Free';
    }
    return (
      packageName.charAt(0).toUpperCase() + packageName.slice(1).toLowerCase()
    );
  };

  const getCurrentPlanFeatures = () => {
    if (!teamPackage) return [];
    const features = planFeatures[teamPackage] || {};
    return Object.entries(features)
      .sort(([, a], [, b]) => a.ranking - b.ranking)
      .map(([, feature]) => feature)
      .slice(0, 13);
  };

  const getPriceDisplay = () => {
    if (!currentPricePerMonth) return '';
    if (term === 'monthly') return ` at $${currentPricePerMonth} per month`;
    return ` at $${currentPrice} per year (equivalent to $${currentPricePerMonth} per month)`;
  };

  // Check if user has available upgrades
  const hasAvailableUpgrades = () => {
    if (isFreeUser) return true; // Free users can always upgrade

    // Check if user is on the highest paid plan
    return teamPackage !== USER_PACKAGE.BUSINESS;
  };

  // Render upgrade options based on user plan
  const renderUpgradeOptions = () => {
    if (isFreeUser) {
      return (
        <SettingsSection
          label={t('settings.billing.upgrade.label', 'Upgrade Plan')}
          description={t(
            'settings.billing.upgrade.description',
            'Get access to premium features by upgrading your plan',
          )}
          showSeparator={false}
        >
          <UpgradeButton />
        </SettingsSection>
      );
    }

    if (hasAvailableUpgrades()) {
      return (
        <SettingsSection
          label="Available Upgrades"
          description="Explore additional features and higher usage limits"
          showSeparator={false}
        >
          <UpsellButton />
        </SettingsSection>
      );
    }

    return null;
  };

  const handleDownloadInvoice = (invoice: IPayment) => {
    dispatch(trackDownloadInvoiceButton({ invoiceId: invoice.id }));
  };

  const renderMobileProviderMessage = () => {
    const platform = subscriptionProvider === 'apple' ? 'iOS' : 'Android';

    return (
      <Card className="border-none bg-muted/50">
        <CardContent className="p-5">
          <div className="text-center space-y-3">
            <h3 className="font-medium text-sm">
              {t('settings.billing.mobileProvider.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('settings.billing.mobileProvider.description', { platform })}
            </p>
            <p className="text-sm font-medium">
              {t('settings.billing.mobileProvider.instruction')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  const getCancelSectionLabel = () => {
    if (isAppleSubscription(providerId)) {
      return t('settings.billing.cancelPlan.label');
    }
    if (isScheduledForCancellation) {
      return t(
        'settings.billing.reactivateSubscription.label',
        'Reactivate Subscription',
      );
    }
    return t('settings.billing.cancelPlan.label');
  };

  const getCancelSectionDescription = () => {
    if (isAppleSubscription(providerId)) {
      return t('settings.billing.cancelPlan.descriptionApple');
    }
    if (isScheduledForCancellation) {
      return t(
        'settings.billing.reactivateSubscription.description',
        'Your subscription is scheduled to cancel on {{date}}. You can reactivate anytime before then.',
        {
          date: cancellationDate
            ? new Date(cancellationDate).toLocaleDateString()
            : 'the end of your billing period',
        },
      );
    }
    return t('settings.billing.cancelPlan.description');
  };

  const renderCancelButton = () => {
    if (isAppleSubscription(providerId)) {
      return (
        <span className="text-muted-foreground text-sm">
          {t('settings.billing.cancelPlan.manageInAppStore')}
        </span>
      );
    }
    if (isScheduledForCancellation) {
      return (
        <Button
          variant="minimal"
          onClick={handleReactivateSubscription}
          disabled={reactivating}
          className="text-black hover:text-black"
        >
          {reactivating
            ? t(
                'settings.billing.reactivateSubscription.reactivating',
                'Reactivating...',
              )
            : t(
                'settings.billing.reactivateSubscription.button',
                'Reactivate Subscription',
              )}
        </Button>
      );
    }
    return (
      <Button
        variant="minimal"
        onClick={handleCancelPlan}
        className="text-black hover:text-black"
      >
        {t('settings.billing.buttons.cancel')}
      </Button>
    );
  };

  const renderPaymentHistory = () => {
    if (loadingPayments) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (paymentError) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{paymentError}</AlertDescription>
        </Alert>
      );
    }
    if (payments.length === 0) {
      return (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">
              {t('settings.billing.paymentHistory.noHistory')}
            </p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-3">
        {payments.map((invoice) => (
          <Card key={invoice.id}>
            <CardContent className="flex items-center justify-between p-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {new Date(invoice.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-sm text-muted-foreground">
                  ${(invoice.amountPaid / 100).toFixed(2)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                asChild
                onClick={() => handleDownloadInvoice(invoice)}
              >
                <a
                  href={invoice.invoicePdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Download</span>
                </a>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <SettingsLayout>
      {/* Mobile Provider Message - Show for paid Apple/Android subscriptions */}
      {isMobileProvider && !isFreeUser && (
        <>
          {renderMobileProviderMessage()}
          <Separator />
        </>
      )}

      {/* Trial Activation - Show for users in trial period */}
      {isTrialing && !isMobileProvider && (
        <>
          <SettingsSection
            label={t('settings.billing.trial.label', 'Trial Period')}
            description={t(
              'settings.billing.trial.description',
              'You are currently in a trial period. Activate now to start your paid subscription immediately.',
            )}
            showSeparator={false}
          >
            <ActivateTrialButton />
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Change Plan - Show to the payer and the team owner */}
      {!isFreeUser && !isMobileProvider && (isPayer || isOwner) && (
        <>
          <SettingsSection
            label={t('settings.billing.changePlan.label')}
            description={t('settings.billing.changePlan.description')}
            showSeparator={false}
          >
            <Button
              variant="minimal"
              onClick={() => setChangePlanModalOpen(true)}
              className="text-black hover:text-black"
            >
              {t('settings.billing.buttons.changePlan')}
            </Button>
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Paid By - Show when a non-owner team member is paying */}
      {!isFreeUser && payer && (
        <>
          <SettingsSection
            label={t('settings.billing.paidBy.label', 'Paid By')}
            description={t(
              'settings.billing.paidBy.description',
              'This subscription is being paid by a team member',
            )}
            showSeparator={false}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{payer.name}</span>
              <span className="text-sm text-muted-foreground">
                ({payer.email})
              </span>
            </div>
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Payment Details - Show only to the payer (or owner if no separate payer) */}
      {!isFreeUser && !isMobileProvider && isPayer && (
        <>
          <SettingsSection
            label={t('settings.billing.paymentDetails.label')}
            description={t('settings.billing.paymentDetails.description')}
            showSeparator={false}
          >
            <Button
              variant="minimal"
              asChild
              className="text-black hover:text-black"
            >
              <a
                href={`${stripePaymentUpdateUrl}?prefilled_email=${settings.user.email}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('settings.billing.buttons.updatePayment')}
              </a>
            </Button>
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Subscription ID - Show for paid users with non-mobile providers only */}
      {!isFreeUser && !isMobileProvider && providerId && (
        <>
          <SettingsSection
            label={t('settings.billing.subscriptionId.label')}
            description={
              isAppleSubscription(providerId)
                ? t('settings.billing.subscriptionId.descriptionApple')
                : t('settings.billing.subscriptionId.description')
            }
            showSeparator={false}
          >
            <div className="flex items-center gap-2">
              {isAppleSubscription(providerId) ? (
                <span className="text-muted-foreground text-sm">
                  {t('settings.billing.subscriptionId.managedThroughApple')}
                </span>
              ) : (
                <>
                  <span className="text-muted-foreground text-sm">
                    {providerId}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() =>
                            navigator.clipboard.writeText(providerId)
                          }
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('settings.billing.subscriptionId.tooltip')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Current Plan */}
      <div className="space-y-4">
        <SettingsSection
          label={t('settings.billing.currentPlan.label')}
          description={`${t('settings.billing.currentPlan.youreCurrently')} ${
            isFreeUser
              ? t('settings.billing.currentPlan.freePlan')
              : `${formatPackageName(teamPackage)} ${
                  term?.charAt(0).toUpperCase() + term?.slice(1)
                } ${t('settings.billing.currentPlan.plan')}${getPriceDisplay()}`
          }`}
          showSeparator={false}
        >
          <div />
        </SettingsSection>

        {/* Plan Features */}
        <div className="bg-card border rounded-lg p-4 space-y-3">
          {getCurrentPlanFeatures().map((feature, index) => (
            <div key={index}>
              <div className="flex items-start gap-3 py-2">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{t(feature.title)}</p>
                  {feature.description && (
                    <p className="text-muted-foreground text-sm">
                      {t(feature.description)}
                    </p>
                  )}
                </div>
              </div>
              {index < getCurrentPlanFeatures().length - 1 && <Separator />}
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Upgrade Options - Show only to the payer */}
      {isPayer &&
        (isFreeUser || (!isMobileProvider && hasAvailableUpgrades())) && (
          <>
            {renderUpgradeOptions()}
            <Separator />
          </>
        )}

      {/* Cancel Plan / Reactivate Subscription - Show to the payer and the team owner */}
      {!isFreeUser && !isMobileProvider && (isPayer || isOwner) && (
        <>
          <SettingsSection
            label={getCancelSectionLabel()}
            description={getCancelSectionDescription()}
            showSeparator={false}
          >
            {renderCancelButton()}
          </SettingsSection>

          {hasActiveDiscount && (
            <div className="mt-4 flex w-fit items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <BadgeInfo className="h-4 w-4 shrink-0" />
              <span>
                {t(
                  'settings.billing.cancelPlan.discountReminder',
                  'Reminder: You have a 50% discount applied to your next billing cycle.',
                )}
              </span>
            </div>
          )}

          {cancelFailed && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
              <span>
                {t(
                  'settings.billing.cancelPlan.error',
                  'Failed to cancel subscription. Please try again or contact support.',
                )}
              </span>
              <Button
                variant="minimal"
                size="sm"
                onClick={() => setSupportDialogOpen(true)}
                className="text-black hover:text-black"
              >
                {t('settings.billing.buttons.contactSupport')}
              </Button>
            </div>
          )}

          {reactivateFailed && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between">
              <span>
                {t(
                  'settings.billing.reactivateSubscription.error',
                  'Failed to reactivate subscription. Please try again or contact support.',
                )}
              </span>
              <Button
                variant="minimal"
                size="sm"
                onClick={() => setSupportDialogOpen(true)}
                className="text-black hover:text-black"
              >
                {t('settings.billing.buttons.contactSupport')}
              </Button>
            </div>
          )}

          <Separator />
        </>
      )}

      {/* Payment History - Show for paid users with non-mobile providers only */}
      {!isFreeUser && !isMobileProvider && (
        <>
          <SettingsSection
            label={t(
              'settings.billing.previousPayments.title',
              'Payment History',
            )}
            description={t(
              'settings.billing.previousPayments.description',
              'View and download your payment history',
            )}
            showSeparator={false}
          >
            <Button
              variant="minimal"
              onClick={handleOpenPaymentHistory}
              className="text-black hover:text-black"
            >
              {t('settings.billing.buttons.viewPayments', 'View Payments')}
            </Button>
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Enterprise Section - Show for paid users with non-mobile providers only */}
      {!isFreeUser && !isMobileProvider && !isPlan(['ENTERPRISE']) && (
        <SettingsSection
          label={t('settings.billing.enterprise.organizationPlan.label')}
          description={t(
            'settings.billing.enterprise.organizationPlan.description',
          )}
          showSeparator={false}
        >
          <Button
            variant="minimal"
            onClick={() => setSalesDialogOpen(true)}
            className="text-black hover:text-black"
          >
            {t('settings.billing.buttons.contactSales')}
          </Button>
        </SettingsSection>
      )}

      <ActionDialog
        title={t('settings.billing.salesDialog.title')}
        open={salesDialogOpen}
        onClose={() => setSalesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        actions={[]}
      >
        <div>
          <ContactSalesForm
            formSource={t('settings.billing.salesDialog.formSource')}
          />
        </div>
      </ActionDialog>

      {supportDialogOpen && (
        <ContactSupportDialog
          onClose={() => setSupportDialogOpen(false)}
          formSource={t('settings.billing.supportDialog.formSource')}
        />
      )}

      {changePlanModalOpen && (
        <ChangePlanModal
          open={changePlanModalOpen}
          onClose={() => setChangePlanModalOpen(false)}
          currentPlan={teamPackage}
          // HACK: ChangePlanModal doesn't support weekly terms, so we pass undefined for weekly subscriptions
          // TODO: When weekly support is added to ChangePlanModal, remove this conditional logic
          currentTerm={term === 'weekly' ? undefined : term}
          currentPrice={currentPrice}
        />
      )}

      {/* Payment History Dialog */}
      <Dialog open={paymentHistoryOpen} onOpenChange={setPaymentHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {t('settings.billing.previousPayments.title', 'Payment History')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'settings.billing.previousPayments.modalDescription',
                'Your complete payment and invoice history',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1">
            {renderPaymentHistory()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Native Cancellation Flow Modals */}
      <CancellationConfirmModal
        open={showConfirmModal}
        onClose={closeAllModals}
        onProceed={handleConfirmProceed}
        cancellationInfo={cancellationInfo}
      />

      {teamId && (
        <StripeSurveyModal
          open={showSurveyModal}
          onClose={closeAllModals}
          onSubmit={handleSurveySubmit}
          teamId={teamId}
        />
      )}

      <RetentionOfferModal
        open={showRetentionModal}
        onClose={closeAllModals}
        onAccept={handleAcceptRetentionOffer}
        onDecline={handleDeclineRetentionOffer}
        retentionOffer={cancellationInfo?.retentionOffer || null}
        subscriptionType={cancellationInfo?.subscriptionType || 'paid'}
      />

      <CancellationSuccessModal
        open={showSuccessModal}
        onClose={closeAllModals}
        cancelAt={cancelDate}
        message={successMessage}
      />
    </SettingsLayout>
  );
};

export default Billing;
